import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  QuoteSubmissionFields,
  buildQuoteLineItemsPayload,
  createQuoteLineDraft,
  type QuoteLineDraft,
} from "@/components/features/notarial-requests/quote-submission-fields";
import { quoteLineDraftsFromItems } from "@/components/features/notarial-requests/quote-line-drafts";
import type { QuoteLineItemView } from "@/services/quoteLineItems";
import { uyPlatformFeeNetMinor, uyVatOnNetMinor } from "@gestionalo/shared-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import toast from "react-hot-toast";
import type { ReactNode } from "react";
import {
  Archive,
  Ban,
  Calendar,
  ExternalLink,
  Info,
  Loader2,
  PauseCircle,
  Upload,
  X,
} from "lucide-react";

import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import {
  showInlineUploadForm,
  showReplaceRowAction,
} from "@/pages/notary/delivered-work-slot";
import { IncomeCertificateSummaryCard } from "@/components/features/notarial-requests/income-certificate-summary-card";
import { ProfessionalRequestPricingCard } from "@/components/features/notarial-requests/professional-request-pricing-card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AuthenticatedBlobDocumentLink } from "@/components/shared/AuthenticatedBlobDocumentLink";
import { LoadingPage } from "@/components/shared/LoadingPage";
import {
  RequestActivityList,
  notarialActivityConfig,
  quoteRequestActivityConfig,
} from "@/components/shared/RequestActivityList";
import { QuoteClarificationsCard } from "@/components/features/notarial-requests/quote-clarifications-card";
import { QuoteDeliveryDestinationCard } from "@/components/features/notarial-requests/quote-delivery-destination-card";
import { QuoteScopeCard } from "@/components/features/notarial-requests/quote-scope-card";
import type { QuoteRequiredDocumentDraft } from "@/components/features/notarial-requests/quote-submission-fields";
import { QuoteMyBidCard } from "@/components/features/notarial-requests/quote-my-bid-card";
import { QuoteBreakdownDialog } from "@/components/features/notarial-requests/quote-breakdown-dialog";
import { QuoteDeadlineChip } from "@/components/features/notarial-requests/quote-deadline-chip";
import {
  contactDetailKindsFromError,
  isFiscalDeclarationRequiredError,
  type ContactDetailKind,
} from "@/services/quoteRequestsService";
import { FiscalDeclarationDialog } from "@/components/features/notarial-requests/fiscal-declaration-dialog";
import { ConsumerCfeGateModal } from "@/components/features/notarial-requests/consumer-cfe-gate-modal";
import { getRfqGate } from "@/services/professionalEarningsService";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { StyledFileInput } from "@/components/shared/StyledFileInput";
import {
  PROFESSIONAL_ATTACHMENT_DOCUMENT_TYPES,
  uploadProfessionalAttachment,
  type ProfessionalAttachmentDocumentType,
} from "@/services/notaryRequestsService";
import {
  StatusBadge,
  type StatusBadgeVariant,
} from "@/components/shared/StatusBadge";
import { FulfillmentStageChip } from "@/components/shared/FulfillmentStageChip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useProfessionalRequestCopy } from "@/hooks/useProfessionalRequestCopy";
import { serverMessage } from "@/lib/apiError";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  notarialDocumentTypeTranslationKey,
  type NotarialCertificateLabelContext,
} from "@/lib/notarialDocumentTypeLabel";
import { useAuth } from "@/contexts/AuthContext";
import {
  getNotaryRequestDetail,
  acceptNotaryRequest,
  setNotaryEstimatedCompletionDate,
  markNotaryRequestCannotComplete,
  requestNotaryRequestInfo,
  markNotaryGestionReady,
  markNotaryShipmentInTransit,
  markNotaryDelivered,
  uploadNotaryCertificate,
  downloadNotaryRequestDocumentsZip,
  fetchNotaryRequestDocumentBlob,
  getQuoteRequestDetailForPro,
  downloadProQuoteRequestAttachment,
  askProQuoteClarification,
  cancelProDocumentRequest,
  createProDocumentRequests,
  getProDocumentRequests,
  getProQuoteFormConfig,
  submitProQuote,
  withdrawProQuote,
  type NotarialRequestStatus,
  type NotarialRequestDocumentDto,
} from "@/services/notaryRequestsService";


import { openAuthenticatedBlobInNewTab } from "@/utils/openAuthenticatedBlobInNewTab";
import { RequestDetailLayout } from "@/components/shared/RequestDetailLayout";
import { ReferenceSection } from "@/components/shared/ReferenceSection";
import { QuoteAttachmentsTable } from "@/components/features/notarial-requests/quote-attachments-table";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { DocumentRequestDialog } from "@/components/features/notarial-requests/document-request-dialog";

const NOTARIAL_ACTIVE_ASSIGNMENT_LIMIT_CODE =
  "notarial_professional_active_assignment_limit";
const NOTARIAL_NOTARY_PENDING_ACTIVATION_CODE =
  "notarial_notary_pending_activation";
const NOTARIAL_ACCOUNTANT_PENDING_ACTIVATION_CODE =
  "notarial_accountant_pending_activation";

/** The backend's `errorCode`, when the failure carries one. */
function errorCodeOf(err: unknown): string | null {
  const response =
    err !== null &&
    typeof err === "object" &&
    "response" in err &&
    (err as { response?: { data?: unknown } }).response != null
      ? (err as { response: { data?: unknown } }).response
      : undefined;
  const data = response?.data;
  if (data !== null && typeof data === "object" && "errorCode" in data) {
    const code = (data as { errorCode?: string }).errorCode;
    return typeof code === "string" ? code : null;
  }
  return null;
}

/**
 * The reasons a professional can be refused work, in the words that explain
 * it.
 *
 * Shared by accepting a trámite and by quoting an RFQ, because the backend
 * refuses both for the same three reasons and a professional blocked by the
 * capacity cap on the RFQ side used to get "no se pudo enviar la cotización" —
 * a failure with no cause, on the one path where the cause is the whole point.
 */
function getProfessionalBlockedMessage(
  err: unknown,
  tp: (notaryRelativePath: string) => string,
): string | null {
  switch (errorCodeOf(err)) {
    case NOTARIAL_ACTIVE_ASSIGNMENT_LIMIT_CODE:
      return tp("requestDetail.acceptBlockedActiveAssignment");
    case NOTARIAL_NOTARY_PENDING_ACTIVATION_CODE:
      return tp("requestDetail.acceptBlockedPendingActivation");
    case NOTARIAL_ACCOUNTANT_PENDING_ACTIVATION_CODE:
      return tp("requestDetail.acceptBlockedAccountantPendingReview");
    default:
      return null;
  }
}

function getAcceptNotaryRequestToastMessage(
  err: unknown,
  tp: (notaryRelativePath: string) => string,
): string {
  const blocked = getProfessionalBlockedMessage(err, tp);
  if (blocked != null) return blocked;

  const response =
    err !== null &&
    typeof err === "object" &&
    "response" in err &&
    (err as { response?: { data?: unknown } }).response != null
      ? (err as { response: { data?: unknown } }).response
      : undefined;
  const data = response?.data;
  if (data !== null && typeof data === "object" && "message" in data) {
    const msg = (data as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim() !== "") {
      return msg;
    }
  }
  return tp("requestDetail.acceptError");
}

/**
 * A detail row for the company dialog.
 *
 * Delegates to the shared `DetailRow` rather than re-implementing it — this
 * used to be its own copy of the grid, which is how a second label width
 * entered the codebase unnoticed.
 *
 * The narrower label column is the one deliberate difference: these rows live
 * in a `DialogContent`, which is materially narrower than the page cards the
 * 160px default was sized for, and at that width the value column starts
 * wrapping company names. It is passed through `className` — the documented
 * way to vary the row — instead of a second component.
 */
function CompanyDetailRow({
  label,
  value,
  bold = false,
}: {
  label: string;
  value: string | null | undefined;
  bold?: boolean;
}): JSX.Element {
  return (
    <DetailRow
      label={label}
      className="grid-cols-[minmax(0,140px)_1fr]"
      valueClassName={bold ? "font-medium" : undefined}
    >
      {value ?? "—"}
    </DetailRow>
  );
}

const STATUS_LABEL_RELATIVE: Record<NotarialRequestStatus, string> = {
  // Defensive — professionals never reach a detail page for this status.
  pending_profile_completion: "requests.statusPending",
  pending_payment_verification: "requests.statusPending",
  pending: "requests.statusPending",
  in_progress: "requests.statusInProgress",
  completed: "requests.statusCompleted",
  missing_information: "requests.statusMissingInfo",
  cannot_complete: "requests.statusCannotComplete",
  document_ready_at_professional: "requests.statusDocumentReadyAtProfessional",
  document_at_platform: "requests.statusDocumentAtPlatform",
  awaiting_consumer_delivery_details: "requests.statusAwaitingConsumerDelivery",
  awaiting_shipment_to_consumer: "requests.statusAwaitingShipment",
  in_transit_to_consumer: "requests.statusInTransitToConsumer",
};

const STATUS_VARIANTS: Record<NotarialRequestStatus, StatusBadgeVariant> = {
  pending_profile_completion: "warning",
  pending_payment_verification: "warning",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  missing_information: "danger",
  cannot_complete: "softDanger",
  document_ready_at_professional: "warning",
  document_at_platform: "info",
  awaiting_consumer_delivery_details: "info",
  awaiting_shipment_to_consumer: "info",
  in_transit_to_consumer: "info",
};

function isIncomeCertificateDependentModality(
  details: Record<string, unknown> | null | undefined,
): boolean {
  if (details == null || typeof details !== "object") return false;
  return details.workModality === "dependent";
}

/**
 * Locks every control inside it while the platform has work on this request
 * paused.
 *
 * A native `disabled` fieldset rather than a prop threaded through twenty
 * buttons: the server actions that answer 409 `request_paused` are spread
 * across the summary cards, the documents column and the activity column, and
 * a per-button flag is a list that the next action added to this page silently
 * falls off. The fieldset disables descendant form controls by DOM descent, so
 * a control added to any of those three regions is locked without anyone
 * remembering to.
 *
 * DOM descent is also its limit. The dialogs render through a React portal, so
 * their buttons are NOT in this subtree and are not disabled by it — they are
 * unreachable instead, because every control that opens one is itself inside a
 * lock. An action added directly to a dialog would need its own guard, and the
 * server's 409 is what stands behind all of this either way.
 *
 * Reading is untouched: links and downloads are anchors, which `disabled`
 * does not reach.
 *
 * The style reset matters. A fieldset carries a border, padding, margin and an
 * intrinsic `min-width: min-content` that would break the grid cells it sits
 * in; `className` takes the spacing class of whatever container it replaces so
 * the rhythm is unchanged.
 *
 * The horizontal-only margin reset (`mx-0`, not `m-0`) is load-bearing. A
 * fieldset's UA margin is `margin-inline: 2px` — horizontal only — while
 * Tailwind v4 implements `space-y-*` as a `:where()` rule, which has ZERO
 * specificity and so loses to any margin utility on the child. `m-0` here
 * therefore silently cancelled the gap the PARENT container was trying to put
 * below this fieldset, which is how the deliverable section ended up flush
 * against the documents grid with no separation at all.
 */
function WorkLock({
  locked,
  className,
  children,
}: {
  locked: boolean;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <fieldset
      disabled={locked}
      className={`mx-0 min-w-0 border-0 p-0 ${className ?? ""}`.trim()}
    >
      {children}
    </fieldset>
  );
}

export function NotaryRequestDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isQuoteChannel = searchParams.get("channel") === "quote";
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget =
    typeof (location.state as { from?: unknown } | null)?.from === "string"
      ? (location.state as { from: string }).from
      : "/requests";
  const { t } = useTranslation();
  const { tp, tpo } = useProfessionalRequestCopy();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const certificateLabelContext: NotarialCertificateLabelContext =
    user?.role === "accountant" || user?.isAccountant === true
      ? "accountant"
      : "notary";

  const [requestInfoOpen, setRequestInfoOpen] = useState(false);
  const [documentRequestOpen, setDocumentRequestOpen] = useState(false);
  const [requestInfoMessage, setRequestInfoMessage] = useState("");
  const [isSubmittingInfo, setIsSubmittingInfo] = useState(false);
  // A file the professional is SENDING, offered inside the request-info
  // dialog: "firmá esto y devolvémelo" is one action to the professional even
  // though it is two calls underneath.
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentType, setAttachmentType] =
    useState<ProfessionalAttachmentDocumentType>("para_firmar");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  /**
   * ONE text field and ONE pending flag for the whole delivery journey, not
   * three of each. The three transitions are mutually exclusive by status —
   * ready is only offered on `in_progress`, en camino only on
   * `document_ready_at_professional`, entregada only on
   * `in_transit_to_consumer` — so at most one of them is on screen at a time,
   * and separate state would differ only in which copy of an empty string was
   * being read.
   */
  const [deliveryMessage, setDeliveryMessage] = useState("");
  const [isTransitioningDelivery, setIsTransitioningDelivery] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  // Which client attachment is being fetched, so only that row spins.
  // The open is a round trip through the API, not a plain link.
  const [openingQuoteAttachmentId, setOpeningQuoteAttachmentId] = useState<
    string | null
  >(null);
  const [companyDetailsOpen, setCompanyDetailsOpen] = useState(false);
  const [cannotCompleteOpen, setCannotCompleteOpen] = useState(false);
  const [quoteLines, setQuoteLines] = useState<QuoteLineDraft[]>(() => [
    createQuoteLineDraft(),
  ]);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  /**
   * Whether the professional is rewriting a bid they have already sent.
   *
   * The form is the SAME one, seeded from the live quote: the endpoint it
   * posts to already upserts (theory 8ba110f), so editing needed no new
   * server call and no second form — only a way back to this one.
   */
  const [editingQuote, setEditingQuote] = useState(false);
  /**
   * Does this job end in paper the professional has to deliver?
   *
   * THEIRS TO DECLARE (spec f462e7d6 3.6), seeded from what the intake
   * guessed. It was briefly derived from the request's own answer with no
   * control at all, which contradicted decisions 851922e2 / 84103aaa and left
   * a trámite triaged as digital with no way to become physical — the awarded
   * child spawns digital_only and mark-ready then refuses it, so the
   * professional ends up holding paper the platform will not let them deliver.
   */
  const [quoteRequiresPhysical, setQuoteRequiresPhysical] = useState(false);
  const [quoteEta, setQuoteEta] = useState("");
  const [quoteRequiredDocuments, setQuoteRequiredDocuments] = useState<
    QuoteRequiredDocumentDraft[]
  >([]);

  /**
   * Opens the bid form over an existing quote, with every field as the
   * professional last left it.
   *
   * SEEDED, not blank. An empty form over a live bid is a trap: submitting it
   * overwrites the real one, because the endpoint upserts. The bid's OWN
   * `requiresPhysicalDelivery` wins over whatever the request guessed — the
   * professional already answered that question once.
   */
  const beginEditingQuote = useCallback(
    (quote: {
      lineItems: QuoteLineItemView[] | null;
      estimatedDays: number | null;
      message: string | null;
      requiresPhysicalDelivery?: boolean;
    }) => {
      setQuoteLines(quoteLineDraftsFromItems(quote.lineItems));
      setQuoteEta(quote.estimatedDays == null ? "" : String(quote.estimatedDays));
      /* Seeded from the intake, not from the quote: the read-back shape does
         not carry requiredDocuments yet (P2 writes it, a later slice returns
         it). Re-seeding on open is the honest behaviour until then — the
         professional sees the same starting list they saw the first time. */
      // A bid that never declared it — one submitted before the field existed
      // — leaves the page's own value alone, which is seeded from what the
      // intake guessed. Only an actual declaration overrides that.
      if (quote.requiresPhysicalDelivery != null) {
        setQuoteRequiresPhysical(quote.requiresPhysicalDelivery);
      }
      setEditingQuote(true);
    },
    [],
  );
  const [clarificationQuestion, setClarificationQuestion] = useState("");
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [clarificationBlockedKinds, setClarificationBlockedKinds] = useState<
    ContactDetailKind[] | null
  >(null);
  // Same shape, separate state: a blocked question and a blocked quote are two
  // different forms on this page, and sharing one would clear the notice on
  // whichever the professional was not editing.
  const [quoteBlockedKinds, setQuoteBlockedKinds] = useState<
    ContactDetailKind[] | null
  >(null);
  const [cannotCompleteNotes, setCannotCompleteNotes] = useState("");
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(
    null,
  );
  const [downloadZipLoading, setDownloadZipLoading] = useState(false);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [isUploadingCertificate, setIsUploadingCertificate] = useState(false);
  const [certificateValidityDate, setCertificateValidityDate] = useState("");
  /** The replace form is a dialog now, reached from the delivered-work row. */
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);

  // Estimated completion date
  const [acceptDateDialogOpen, setAcceptDateDialogOpen] = useState(false);
  const [acceptDateValue, setAcceptDateValue] = useState("");
  const [estimatedDateDialogOpen, setEstimatedDateDialogOpen] = useState(false);
  const [estimatedDateValue, setEstimatedDateValue] = useState("");
  const [estimatedDateReason, setEstimatedDateReason] = useState("");
  const [isSubmittingEstimatedDate, setIsSubmittingEstimatedDate] =
    useState(false);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["notary", "requests"] });
    void queryClient.invalidateQueries({ queryKey: ["notary", "request", id] });
  }, [queryClient, id]);

  const { data: request, isLoading } = useQuery({
    queryKey: ["notary", "request", id],
    queryFn: () => getNotaryRequestDetail(id!),
    enabled: Boolean(id) && !isQuoteChannel,
    refetchOnWindowFocus: false,
  });

  const quoteQueryClient = useQueryClient();
  const { data: quoteRequest, isLoading: isQuoteLoading } = useQuery({
    queryKey: ["pro", "quote-request", id],
    queryFn: () => getQuoteRequestDetailForPro(id!),
    enabled: Boolean(id) && isQuoteChannel,
    refetchOnWindowFocus: false,
  });

  // Only for the bidding form's live total. Both indices are scraped and
  // cached server-side, so this is cheap — but it is still a network read the
  // consumer-side and non-quote branches have no use for.
  const { data: quoteFormConfig } = useQuery({
    queryKey: ["pro", "quote-form-config"],
    queryFn: getProQuoteFormConfig,
    enabled: isQuoteChannel,
    // A UR index moves monthly at the fastest, and a stale value only affects
    // a preview — the server resolves the real one at submission.
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // What has already been asked for. Its own query rather than part of the
  // request detail: it changes on its own schedule — a consumer upload closes
  // a slot without touching the request — and the detail is heavy.
  const { data: documentRequestsData } = useQuery({
    queryKey: ["notary", "request", id, "document-requests"],
    queryFn: () => getProDocumentRequests(id!),
    enabled: Boolean(id) && !isQuoteChannel,
    refetchOnWindowFocus: false,
  });
  const documentRequests = documentRequestsData?.documents ?? [];

  const cancelDocumentRequestMutation = useMutation({
    mutationFn: (documentRequestId: string) =>
      cancelProDocumentRequest(id!, documentRequestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notary", "request", id, "document-requests"],
      });
    },
    onError: () => {
      toast.error(t("notary.requestDetail.documentRequestCancelError"));
    },
  });

  const documentRequestMutation = useMutation({
    mutationFn: (documents: Parameters<typeof createProDocumentRequests>[1]) =>
      createProDocumentRequests(id!, documents),
    onSuccess: () => {
      toast.success(t("notary.requestDetail.documentRequestSuccess"));
      setDocumentRequestOpen(false);
      // The request moved to missing_information and the timeline gained an
      // event, so the detail has to be refetched rather than patched.
      void queryClient.invalidateQueries({
        queryKey: ["notary", "request", id],
      });
      void queryClient.invalidateQueries({ queryKey: ["notary", "requests"] });
    },
    onError: () => {
      toast.error(t("notary.requestDetail.documentRequestError"));
    },
  });

  /**
   * Set when the server refuses the quote for want of a fiscal declaration.
   * Renders the form inline, which is what keeps that refusal a gate rather
   * than a dead end — no professional holds a RUT yet, so every one of them
   * meets it on their next quote.
   */
  const [fiscalDeclarationRequired, setFiscalDeclarationRequired] = useState(false);
  const [fiscalDialogOpen, setFiscalDialogOpen] = useState(false);

  const submitQuoteMutation = useMutation({
    mutationFn: () =>
      submitProQuote(id!, {
        lineItems: buildQuoteLineItemsPayload(quoteLines),
        // Always true since spec 76e23408 D6. The field stays on the wire and
        // on the row because the column is persisted, but it no longer varies
        // and nothing reads it in the arithmetic: what the professional
        // entered IS the final figure for their part. It defaulted to FALSE
        // here, which is how a professional who simply never ticked the box
        // had 22% added to their client's bill.
        pricesIncludeIva: true,
        // THE PROFESSIONAL'S OWN declaration (spec f462e7d6 3.6), seeded from
        // the intake's guess. Whether a trámite yields a testimonio is their
        // knowledge, not the triage agent's (851922e2, 84103aaa).
        requiresPhysicalDelivery: quoteRequiresPhysical,
        estimatedDays: quoteEta.trim() ? Number(quoteEta) : undefined,
        /* `seeded` is a rendering concern and stays here; the server decides
           provenance against the intake draft (control C-3). */
        requiredDocuments: quoteRequiredDocuments.map(({ label, kind, documentType }) => ({
          label,
          kind,
          documentType,
        })),
      }),
    onSuccess: () => {
      setQuoteBlockedKinds(null);
      setFiscalDeclarationRequired(false);
      // Back to the card. Leaving the form open after a successful save would
      // show the professional an editable copy of what they just sent, with
      // no sign it landed anywhere.
      setEditingQuote(false);
      toast.success(tp("requestDetail.quoteSubmissionSuccess"));
      void quoteQueryClient.invalidateQueries({
        queryKey: ["pro", "quote-request", id],
      });
      void quoteQueryClient.invalidateQueries({
        queryKey: ["notary", "requests"],
      });
    },
    onError: (err) => {
      // Moderation refuses the quote's free text for the same reason it
      // refuses a question, and it is shown the same way: inline, next to the
      // fields, because it is a thing to edit rather than an error to dismiss.
      const kinds = contactDetailKindsFromError(err);
      if (kinds) {
        setQuoteBlockedKinds(kinds);
        return;
      }
      // Shown as a FORM, not a toast. The professional can resolve this in
      // place and send; a toast would state a requirement with no way to meet
      // it, which for this particular refusal means nobody can quote at all.
      if (isFiscalDeclarationRequiredError(err)) {
        setQuoteBlockedKinds(null);
        setFiscalDeclarationRequired(true);
        // Opened immediately: they pressed Send a moment ago, so making them
        // click a second time to reach the form adds a step to an interruption
        // they did not ask for. The notice behind it is what they return to if
        // they dismiss.
        setFiscalDialogOpen(true);
        return;
      }
      setQuoteBlockedKinds(null);
      // The cap and the activation checks both refuse here now, and both have
      // an answer the professional can act on — unlike the generic failure.
      toast.error(
        getProfessionalBlockedMessage(err, tp) ?? tp("requestDetail.quoteSubmissionError"),
      );
    },
  });

  const withdrawQuoteMutation = useMutation({
    mutationFn: () => withdrawProQuote(id!, quoteRequest!.myQuote!.id),
    onSuccess: () => {
      setWithdrawConfirmOpen(false);
      toast.success(tp("requestDetail.quoteWithdrawSuccess"));
      void quoteQueryClient.invalidateQueries({
        queryKey: ["pro", "quote-request", id],
      });
      void quoteQueryClient.invalidateQueries({
        queryKey: ["notary", "requests"],
      });
    },
    onError: () => {
      setWithdrawConfirmOpen(false);
      toast.error(tp("requestDetail.quoteWithdrawError"));
    },
  });

  /**
   * Moderation refuses a message with 400 `contact_details_blocked`. That is a
   * normal outcome of this form, not a failure of it, so it is shown inline
   * next to what the professional wrote rather than as a toast that disappears.
   */
  const askClarificationMutation = useMutation({
    mutationFn: (question: string) => askProQuoteClarification(id!, question),
    onSuccess: () => {
      setClarificationBlockedKinds(null);
      // Cleared only here: a refused question stays in the box so its author
      // can edit out what was detected instead of writing it again.
      setClarificationQuestion("");
      toast.success(t("requests.clarifications.askSuccess"));
      void quoteQueryClient.invalidateQueries({
        queryKey: ["pro", "quote-request", id],
      });
    },
    onError: (error: unknown) => {
      const kinds = contactDetailKindsFromError(error);
      if (kinds) {
        setClarificationBlockedKinds(kinds);
        // The thread refetch surfaces the blocked attempt itself, marked, so a
        // message that never arrived is visible rather than merely reported.
        void quoteQueryClient.invalidateQueries({
          queryKey: ["pro", "quote-request", id],
        });
        return;
      }
      setClarificationBlockedKinds(null);
      toast.error(t("requests.clarifications.askError"));
    },
  });

  /**
   * EVERY REMAINING HOOK LIVES ABOVE THE `isQuoteChannel` RETURN BELOW, AND
   * THAT IS LOAD-BEARING. Do not move one back down.
   *
   * `isQuoteChannel` reads a SEARCH param, and React Router does not remount
   * on a query-string change — so navigating between
   * `/requests/:id?channel=quote` and `/requests/:id` flips that branch on a
   * live component instance. Any hook below the branch would be called on one
   * render and not the next, which is React error #310.
   *
   * That is not hypothetical here: it happened on 2026-10-09 with the
   * `rfqGate` query and took this page down for every professional. The 17
   * hooks below were in the same position and had simply not been hit yet;
   * `react-hooks/rules-of-hooks` found them the day the linter was turned on.
   *
   * The quote branch itself declares NO hooks, so hoisting these costs only a
   * few cheap memo/callback allocations on the quote path. The effect below
   * only resets a message field the quote view never renders, and the one
   * query is already `enabled: !isQuoteChannel`.
   */
  /**
   * Seed the physicality control from the server, once per request.
   *
   * THEIR OWN prior declaration wins over the intake's guess: a professional
   * re-opening a withdrawn bid already answered this question, and resetting
   * it to what triage thought would silently undo their correction.
   *
   * Keyed on the request id rather than on the payload, so a refetch while
   * they are mid-edit does not yank the switch out from under them.
   */
  const seededPhysicalFor = useRef<string | null>(null);
  const seededRequiredDocsFor = useRef<string | null>(null);
  useEffect(() => {
    if (quoteRequest == null) return;
    if (seededPhysicalFor.current === quoteRequest.id) return;
    seededPhysicalFor.current = quoteRequest.id;
    setQuoteRequiresPhysical(
      quoteRequest.myQuote?.requiresPhysicalDelivery ??
        quoteRequest.producesPhysicalDocument === true,
    );
  }, [quoteRequest]);

  /* Seed the required-document picker from what the intake anticipated, once
     per request (spec 18c82ff1 §5a).

     Mirrors the physical-delivery seeding above deliberately: same ref-guard,
     same "the platform proposes, the professional disposes" shape. Seeding on
     every render would fight the professional as they remove items, and
     seeding on mount alone would miss the request arriving after first paint.

     Everything lands marked `seeded` so the form can label it — that flag is
     local rendering only and never reaches the server, which decides
     provenance itself against the same draft (control C-3). */
  useEffect(() => {
    if (quoteRequest == null) return;
    if (seededRequiredDocsFor.current === quoteRequest.id) return;
    seededRequiredDocsFor.current = quoteRequest.id;
    setQuoteRequiredDocuments(
      (quoteRequest.anticipatedDocuments ?? []).map((doc) => ({
        label: doc.label,
        kind: doc.kind,
        documentType: doc.documentType,
        seeded: true,
      })),
    );
  }, [quoteRequest]);

  const newestVisibleEventType = useMemo(() => {
    if (request?.events == null) return null;
    const sorted = [...request.events].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return sorted[0]?.eventType ?? null;
  }, [request?.events]);

  // Shared by the page title and the "Tipo" field below so they can never
  // show two different labels for the same request.
  const requestTypeLabel = useMemo(() => {
    if (!request) return "";
    const typeKey = request.requestType?.trim() || "notarial_certificate";
    // Falls back to the raw slug, not another type's real label -- an
    // unrecognized/future requestType should read as unlabeled, never as an
    // actively wrong certificate name (this previously defaulted to
    // "notarial_certificate"'s label for every unmapped type, including
    // RFQ-spawned "custom_quote" requests).
    const typeLabel = t(`notary.requests.requestType_${typeKey}`, {
      defaultValue: typeKey,
    });
    return request.sourceRequestTitle
      ? `${typeLabel} - ${request.sourceRequestTitle}`
      : typeLabel;
  }, [request, t]);

  const requestPageTitle = useMemo(() => {
    if (!request) return "";
    return `${tp("requestDetail.pageTitlePrefix")}${requestTypeLabel}`;
  }, [request, requestTypeLabel, tp]);

  const showStatusVersusHistoryHint =
    request != null &&
    request.status === "document_at_platform" &&
    newestVisibleEventType === "physical_document_ready";

  const cannotCompleteMutation = useMutation({
    mutationFn: (notes: string) => markNotaryRequestCannotComplete(id!, notes),
    onSuccess: () => {
      toast.success(tp("requestDetail.cannotCompleteSuccess"));
      setCannotCompleteOpen(false);
      setCannotCompleteNotes("");
      invalidate();
    },
    onError: () => {
      toast.error(tp("requestDetail.cannotCompleteError"));
    },
  });

  const handleAcceptClick = useCallback(() => {
    setAcceptDateValue("");
    setAcceptDateDialogOpen(true);
  }, []);

  const handleAcceptConfirm = useCallback(
    async (withDate: boolean) => {
      if (!id) return;
      setAcceptDateDialogOpen(false);
      setIsAccepting(true);
      try {
        const body =
          withDate && acceptDateValue
            ? {
                estimatedCompletionDate: new Date(
                  acceptDateValue + "T00:00:00Z",
                ).toISOString(),
              }
            : undefined;
        await acceptNotaryRequest(id, body);
        toast.success(tp("requestDetail.accepted"));
        invalidate();
      } catch (err: unknown) {
        toast.error(getAcceptNotaryRequestToastMessage(err, tp));
      } finally {
        setIsAccepting(false);
      }
    },
    [id, invalidate, tp, acceptDateValue],
  );

  const handleSetEstimatedDate = useCallback(async () => {
    if (!id || !estimatedDateValue) return;
    const isUpdate = request?.estimatedCompletionDate != null;
    if (isUpdate && !estimatedDateReason.trim()) {
      toast.error(tp("requestDetail.estimatedDateReasonRequired"));
      return;
    }
    setIsSubmittingEstimatedDate(true);
    try {
      await setNotaryEstimatedCompletionDate(id, {
        estimatedCompletionDate: new Date(
          estimatedDateValue + "T00:00:00Z",
        ).toISOString(),
        reason: estimatedDateReason.trim() || undefined,
      });
      toast.success(
        isUpdate
          ? tp("requestDetail.estimatedDateUpdated")
          : tp("requestDetail.estimatedDateSet"),
      );
      setEstimatedDateDialogOpen(false);
      setEstimatedDateValue("");
      setEstimatedDateReason("");
      invalidate();
    } catch {
      toast.error(tp("requestDetail.estimatedDateError"));
    } finally {
      setIsSubmittingEstimatedDate(false);
    }
  }, [
    id,
    estimatedDateValue,
    estimatedDateReason,
    invalidate,
    tp,
    request?.estimatedCompletionDate,
  ]);

  const handleDownloadAllDocumentsZip = useCallback(async (): Promise<void> => {
    if (!id) return;
    setDownloadZipLoading(true);
    try {
      await downloadNotaryRequestDocumentsZip(id);
    } catch {
      toast.error(tp("requestDetail.downloadAllDocumentsZipError"));
    } finally {
      setDownloadZipLoading(false);
    }
  }, [id, tp]);

  const handleUploadCertificate = useCallback(async (): Promise<void> => {
    if (!id || !certificateFile) return;
    setIsUploadingCertificate(true);
    const file = certificateFile;
    const validityIso = certificateValidityDate
      ? new Date(certificateValidityDate + "T00:00:00Z").toISOString()
      : undefined;
    try {
      await uploadNotaryCertificate(id, file, validityIso);
      toast.success(
        tp("requestDetail.uploadCertificateSuccess"),
      );
      setCertificateFile(null);
      setCertificateValidityDate("");
      setReplaceDialogOpen(false);
      invalidate();
    } catch (error) {
      try {
        const refreshed = await getNotaryRequestDetail(id);
        const hasCertificate = refreshed.documents.some(
          (d) => d.documentType === "notarial_certificate",
        );
        if (hasCertificate) {
          toast.success(
            tp("requestDetail.uploadCertificateSuccess"),
          );
          setCertificateFile(null);
          // The upload DID land; the error was secondary. Same close as the
          // happy path, or the dialog sits open over a table already showing
          // the new file.
          setReplaceDialogOpen(false);
          invalidate();
          return;
        }
      } catch {
        // ignore secondary failure
      }
      // The server refuses this upload for stated, actionable reasons (wrong
      // phase, closed correction window). That sentence is the only part of
      // the response that tells the professional what to do next.
      toast.error(
        serverMessage(error) ??
          tp("requestDetail.uploadCertificateError"),
      );
    } finally {
      setIsUploadingCertificate(false);
    }
  }, [
    id,
    certificateFile,
    certificateValidityDate,
    invalidate,
    tp,
  ]);

  /**
   * Send the message, with an optional file attached.
   *
   * Two calls, upload first: that keeps request-info as JSON and means a
   * failed upload does not discard prose the professional has just typed.
   *
   * The two are NOT atomic, so their failures are reported differently on
   * purpose. If the upload fails, nothing has happened and the dialog stays
   * open with everything intact. If the upload SUCCEEDS and the message then
   * fails, the file is already on the request — saying only "no se pudo
   * enviar" would leave the professional believing nothing was sent, and they
   * would attach it again. So that case says the document was attached and
   * the message was not, and clears the file to stop a duplicate.
   */
  const handleSubmitRequestInfo = useCallback(async () => {
    if (!id || !requestInfoMessage.trim()) return;
    setIsSubmittingInfo(true);
    let attachmentIds: string[] | undefined;
    try {
      if (attachmentFile) {
        setIsUploadingAttachment(true);
        try {
          const saved = await uploadProfessionalAttachment(
            id,
            attachmentFile,
            attachmentType,
            undefined,
            // A message follows, so the server suppresses its own push.
            true,
          );
          attachmentIds = [saved.id];
        } finally {
          setIsUploadingAttachment(false);
        }
      }
    } catch {
      toast.error(tp("requestDetail.attachmentUploadError"));
      setIsSubmittingInfo(false);
      return;
    }

    try {
      await requestNotaryRequestInfo(
        id,
        requestInfoMessage.trim(),
        attachmentIds,
      );
      toast.success(tp("requestDetail.infoSent"));
      setRequestInfoOpen(false);
      setRequestInfoMessage("");
      setAttachmentFile(null);
      invalidate();
    } catch {
      if (attachmentIds) {
        toast.error(tp("requestDetail.attachmentSentMessageFailed"));
        // Already on the request — keep it out of a retry.
        setAttachmentFile(null);
        invalidate();
      } else {
        toast.error(tp("requestDetail.infoError"));
      }
    } finally {
      setIsSubmittingInfo(false);
    }
  }, [
    id,
    requestInfoMessage,
    attachmentFile,
    attachmentType,
    invalidate,
    tp,
  ]);

  /**
   * The three transitions the professional owns (spec f462e7d6), as one
   * function because they differ only in which call they make and which copy
   * they report. NOTHING IS ASKED OF THE PROFESSIONAL BUT A MESSAGE: the
   * address is the consumer's, collected at intake, and the whole point of
   * this slice is that nobody types a pickup address ever again.
   */
  const runDeliveryTransition = useCallback(
    async (step: "ready" | "inTransit" | "delivered") => {
      if (!id) return;
      const note = deliveryMessage.trim() || undefined;
      setIsTransitioningDelivery(true);
      try {
        if (step === "ready") {
          await markNotaryGestionReady(id, { notes: note });
        } else if (step === "inTransit") {
          await markNotaryShipmentInTransit(id, { consumerMessage: note });
        } else {
          await markNotaryDelivered(id, { trackingNotes: note });
        }
        toast.success(
          tp(
            step === "ready"
              ? "requestDetail.gestionMarkReadySuccess"
              : step === "inTransit"
                ? "requestDetail.gestionInTransitSuccess"
                : "requestDetail.gestionDeliveredSuccess",
          ),
        );
        setDeliveryMessage("");
        invalidate();
      } catch (error) {
        // The server's refusal is the actionable part — "la gestión tiene que
        // estar lista antes de marcarla en camino" tells the professional what
        // to do; a generic toast does not.
        toast.error(
          serverMessage(error) ??
            tp(
              step === "ready"
                ? "requestDetail.gestionMarkReadyError"
                : step === "inTransit"
                  ? "requestDetail.gestionInTransitError"
                  : "requestDetail.gestionDeliveredError",
            ),
        );
      } finally {
        setIsTransitioningDelivery(false);
      }
    },
    [id, deliveryMessage, invalidate, tp],
  );

  // A half-typed message does not belong to the next request, or to the next
  // step of this one — the status moving under it is what clears it.
  useEffect(() => {
    setDeliveryMessage("");
  }, [id, request?.status]);

  /**
   * Is this an RFQ job whose CFE is still unrecorded (spec 300908fc slice 3b,
   * D3)?
   *
   * ASKED OF THE SERVER, never derived here. Every write on this page is gated
   * server-side, and a screen that decided this for itself would eventually
   * disagree with the gate that enforces it — putting a modal over an action
   * that would have worked, or none over one that will not.
   *
   * A non-RFQ request answers `blocked: false` and costs one cheap call; that
   * is better than guessing from the payload which requests were quoted.
   *
   * ABOVE THE EARLY RETURNS, and it has to stay there. This sat below them
   * until 2026-10-09 and crashed the page with React #310 for every
   * professional on every request: the first render returns <LoadingPage/>
   * before reaching this line, the second one reaches it, and a render that
   * calls one more hook than the last is exactly what #310 reports. `enabled`
   * does not help — it gates the FETCH, not the hook call. Neither tsc nor
   * vite build can see this; only react-hooks/rules-of-hooks can.
   */
  const { data: rfqGate, refetch: refetchRfqGate } = useQuery({
    queryKey: ["pro", "rfq-gate", id],
    queryFn: () => getRfqGate(id!),
    enabled: Boolean(id) && !isQuoteChannel,
    refetchOnWindowFocus: false,
  });
  const cfeGateBlocked = rfqGate?.blocked === true;

  if (isQuoteChannel) {
    if (isQuoteLoading || !quoteRequest) {
      return <LoadingPage />;
    }
    const myQuote = quoteRequest.myQuote;
    const quoteAttachments = quoteRequest.attachments ?? [];
    const quoteProvidedDocumentTypes = quoteAttachments
      .map((a) => a.documentType)
      .filter((d): d is string => d != null);
    const openQuoteAttachment = async (attachmentId: string): Promise<void> => {
      setOpeningQuoteAttachmentId(attachmentId);
      try {
        await openAuthenticatedBlobInNewTab(() =>
          downloadProQuoteRequestAttachment(quoteRequest.id, attachmentId),
        );
      } catch {
        toast.error(tp("requestDetail.quoteAttachmentOpenError"));
      } finally {
        setOpeningQuoteAttachmentId(null);
      }
    };
    // The server refuses a question once the deadline passes, so the composer
    // goes with it — offering a form whose submit is guaranteed to fail is
    // worse than showing the thread read-only.
    const quoteWindowOpen =
      quoteRequest.quotingExpiresAt == null ||
      new Date(quoteRequest.quotingExpiresAt).getTime() > Date.now();
    /*
      THE WINDOW IS PART OF THIS, and it was not. The page printed "El plazo
      cerró" and then rendered the whole pricing form underneath it, enabled
      submit and all — so a professional could cost the job, write a message,
      and learn at submit that the window had shut. submitOrUpdate refuses
      both a new bid and an update past quotingExpiresAt (quote-bidding.
      service.ts), which is deliberate and stays: the deadline is enforced
      THERE and nowhere else. Awarding past it is LEGAL for another 48 hours
      — `awardDeadlineAt` is quotingExpiresAt plus an award grace, and
      QuoteExpirySchedulerService is what ends the leg when that passes, so
      awardQuote refuses on STATUS rather than on a clock. The client's job
      is only to stop offering the one thing the server will refuse: a bid.
    */
    // `editingQuote` is the third way in. The first two are the only states
    // that EVER showed the form; editing is a live bid being rewritten, which
    // the endpoint accepts as an upsert (theory 8ba110f) and which nothing
    // here used to allow. The window still gates all three — the server
    // refuses any submission past the deadline, new or an update.
    const canSubmit =
      (myQuote == null || myQuote.status === "withdrawn" || editingQuote) && quoteWindowOpen;
    const quoteActivityConfig = quoteRequestActivityConfig("professional");
    const quoteActivityEvents = (quoteRequest.events ?? []).map((e) => ({
      id: e.id,
      type: e.eventType,
      createdAt: e.createdAt,
      actorDisplayName: e.actorDisplayName,
      actorRole: e.actorRole,
      message: e.message,
    }));
    // A deadline, rendered as a deadline. It used to be a bold sentence in
    // the same visual register as the description, which made "13 sep" a date
    // the professional had to convert into "how long have I got" themselves.
    const quoteDeadlineNote = quoteRequest.quotingExpiresAt ? (
      <span className="block">
        <QuoteDeadlineChip expiresAt={quoteRequest.quotingExpiresAt} />
      </span>
    ) : null;

    // Documents left, timeline right — the same footer grid every other
    // request-detail surface uses. This branch was stacking both full-width.
    const quoteAttachmentsCard =
      quoteAttachments.length > 0 ? (
        <ReferenceSection label={tp("requestDetail.quoteContextTitle")}>
          {/* The SAME table the consumer sees on their side of this request
              (QuoteAttachmentsTable). This surface used to hand-roll a stack of
              clickable cards: no date column, and no per-row loading state, so
              a slow open looked like a click that did nothing — the exact
              problem already fixed for consumers. No onDelete is passed, and
              that omission is the read-only guarantee: these are the client's
              files. */}
          <QuoteAttachmentsTable
            attachments={quoteAttachments}
            certificateLabelContext={certificateLabelContext}
            onOpen={(attachmentId) => void openQuoteAttachment(attachmentId)}
            openingAttachmentId={openingQuoteAttachmentId}
          />
        </ReferenceSection>
      ) : undefined;

    // The card renders nothing when the window is closed and nothing was ever
    // asked. A JSX element is always truthy, so the pairing below needs the
    // condition, not the element, or it reserves half a row for an invisible
    // component.
    const showQuestionsCard =
      quoteWindowOpen || (quoteRequest.clarifications ?? []).length > 0;
    // The rail treatment — a 20rem floor, a viewport ceiling, sticky — belongs
    // to the card only while it IS a rail, so it keys off the same flag the
    // pairing uses and the two cannot disagree.
    //
    // Bidding does not end the pairing. It swaps the form for "Tu cotización",
    // and the conversation stays where it was: a professional who has bid
    // still gets asked things, and moving the thread to full width the moment
    // they submit rearranges the page under them for no reason.
    //
    // Expressed from the predicates rather than from the two JSX consts, which
    // are declared below. `hasLiveBid` and `canSubmit` are complements today,
    // so this is always true — but stating it this way means it stays correct
    // if either rule gains a condition (a closed quoting window, say) instead
    // of pairing the thread against an empty column.
    const hasLiveBid = myQuote != null && myQuote.status !== "withdrawn";
    /*
      THE WINDOW BEING OPEN IS NO LONGER ENOUGH to offer Editar and Retirar.

      An awarded leg refuses both calls — submitOrUpdate needs the leg to be
      'quoting', withdrawQuote needs the quote to be 'submitted' — and the
      server now reports an unpaid award AS 'submitted', deliberately, so the
      professional is not told they were chosen before the money lands (spec
      award-commits-nothing-until-payment). Gating on status and the clock
      alone would therefore leave both buttons up on exactly that leg, and the
      400 they return would announce the award we are withholding.

      Absent reads as true, for a server that predates the flag.
    */
    const canModifyQuote = myQuote?.canModify !== false;
    const quoteCardsArePaired = showQuestionsCard && (hasLiveBid || canSubmit);
    const quoteQuestionsCard = showQuestionsCard ? (
      <QuoteClarificationsCard
        clarifications={quoteRequest.clarifications ?? []}
        viewer="professional"
        fillHeight={quoteCardsArePaired}
        canCompose={quoteWindowOpen}
        question={clarificationQuestion}
        onQuestionChange={setClarificationQuestion}
        onAsk={(question) => askClarificationMutation.mutate(question)}
        isAsking={askClarificationMutation.isPending}
        blockedKinds={clarificationBlockedKinds}
      />
    ) : null;

    // Whichever of these two exists owns the main column. They are mutually
    // exclusive by construction — canSubmit is false precisely when a live bid
    // exists — but both are rendered into the stack rather than picked with a
    // ??, so a future change to that rule cannot silently drop one.
    const quoteMyBidCard =
      hasLiveBid && myQuote && !editingQuote ? (
        <QuoteMyBidCard
          priceMinor={myQuote.priceMinor}
          lineItems={myQuote.lineItems}
          pricesIncludeIva={myQuote.pricesIncludeIva}
          currency={myQuote.currency}
          estimatedDays={myQuote.estimatedDays}
          message={myQuote.message}
          status={myQuote.status}
          awardPaid={myQuote.awardPaid ?? false}
          /*
            NOT OFFERED PAST THE DEADLINE. Withdrawing is reversible only by
            submitting again, and submitOrUpdate refuses that once the window
            is shut — so the button would turn a live bid into nothing, with
            no way back. The note says why rather than leaving a control
            mysteriously missing.
          */
          onWithdraw={
            myQuote.status === "submitted" && quoteWindowOpen && canModifyQuote
              ? () => setWithdrawConfirmOpen(true)
              : undefined
          }
          /* Reading, not changing — so it survives the deadline. */
          onViewBreakdown={() => setBreakdownOpen(true)}
          /*
            SAME GATE AS WITHDRAW, for the same reason: submitOrUpdate refuses
            an update once the window is shut, so an edit form past the
            deadline is a form whose save the server will reject.
          */
          onEdit={
            myQuote.status === "submitted" && quoteWindowOpen && canModifyQuote
              ? () => beginEditingQuote(myQuote)
              : undefined
          }
          closedWindowNote={
            myQuote.status === "submitted" && !quoteWindowOpen
              ? quoteRequest.awardDeadlineAt
                ? tpo("requestDetail.quoteClosedStillInPlayUntil", {
                    date: formatDisplayDate(quoteRequest.awardDeadlineAt),
                  })
                : tp("requestDetail.quoteClosedStillInPlay")
              : undefined
          }
          isWithdrawing={withdrawQuoteMutation.isPending}
          tp={tp}
          tpo={tpo}
        />
      ) : null;

    /*
      Said out loud, not left as an empty column. With the form gone there is
      nothing in the main slot, and a professional who followed a notification
      here would be looking at a page that simply has no way to quote and no
      statement of why.
    */
    const quoteClosedCard =
      !quoteWindowOpen && !hasLiveBid ? (
        <ReferenceSection label={tp("requestDetail.quoteClosedTitle")}>
          <p className="text-sm text-muted-foreground">
            {/* With the date when we have it. It is null only on legs
                broadcast before awardDeadlineAt was stored, so the dateless
                line stays as the fallback rather than being dropped. */}
            {quoteRequest.awardDeadlineAt
              ? tpo("requestDetail.quoteClosedBodyUntil", {
                  date: formatDisplayDate(quoteRequest.awardDeadlineAt),
                })
              : tp("requestDetail.quoteClosedBody")}
          </p>
        </ReferenceSection>
      ) : null;

    /**
     * The platform's cut on this bid, for the breakdown dialog.
     *
     * NULL WHEN THE CONFIG HAS NO PERCENT, and the dialog then omits every
     * consumer-facing row rather than printing a 0% fee — a fee shown as zero
     * tells a professional their client pays exactly what they receive, which
     * is false and is about someone's pay (spec 031fbf43, C-1).
     *
     * Through the shared helpers, never a local percentage: the same two
     * functions compute the charge the server actually takes, and
     * quote-fee-single-source.spec.ts fails the build if either is
     * re-implemented here.
     */
    const myQuoteFee =
      myQuote == null || quoteFormConfig?.platformFeePercent == null
        ? null
        : (() => {
            const netMinor = uyPlatformFeeNetMinor(
              myQuote.priceMinor,
              quoteFormConfig.platformFeePercent,
            );
            return { netMinor, ivaMinor: uyVatOnNetMinor(netMinor) };
          })();

    const quoteBreakdownDialog =
      myQuote != null ? (
        <QuoteBreakdownDialog
          open={breakdownOpen}
          onOpenChange={setBreakdownOpen}
          priceMinor={myQuote.priceMinor}
          currency={myQuote.currency}
          lineItems={myQuote.lineItems}
          pricesIncludeIva={myQuote.pricesIncludeIva}
          fee={myQuoteFee}
        />
      ) : null;

    const quoteSubmissionCard = canSubmit ? (
      <QuoteSubmissionFields
        lines={quoteLines}
        onLinesChange={setQuoteLines}
        config={quoteFormConfig ?? null}
        requiresPhysicalDelivery={quoteRequiresPhysical}
        onRequiresPhysicalDeliveryChange={setQuoteRequiresPhysical}
        hasDeliveryAddress={
          (quoteRequest?.delivery?.formattedAddress ?? "").trim() !== ""
        }
        deliveryFeeMinor={quoteRequest?.delivery?.feeMinor ?? null}
        estimatedDays={quoteEta}
        onEstimatedDaysChange={setQuoteEta}
        requiredDocuments={quoteRequiredDocuments}
        onRequiredDocumentsChange={setQuoteRequiredDocuments}
        disabled={submitQuoteMutation.isPending}
        isSubmitting={submitQuoteMutation.isPending}
        isEditing={editingQuote}
        /* Discards the draft and puts the card back. Nothing was sent, so the
           live quote is exactly as it was. */
        onCancelEdit={() => setEditingQuote(false)}
        onSubmit={() => submitQuoteMutation.mutate()}
        blockedKinds={quoteBlockedKinds}
      />
    ) : null;

    /**
     * Replaces the quote form while a declaration is owed, rather than sitting
     * beside it. The professional cannot send until this is answered, so
     * leaving an active Send button next to it would invite them to press a
     * thing that refuses them again.
     *
     * On save it retries the submission they already composed — the draft is
     * still in state, so the interruption costs them one form and nothing else.
     */
    /**
     * While a declaration is owed the quote form is replaced by a NOTICE, and
     * the form itself moved into a dialog the notice opens.
     *
     * The replacement is the part that must not be lost: leaving an active
     * Send button on screen invites the professional to press a thing that
     * refuses them again. A dialog on its own would reinstate exactly that,
     * because closing it reveals the live form underneath — so the notice
     * stays, and dismissing the dialog returns to the notice.
     */
    const quoteCard = fiscalDeclarationRequired ? (
      <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-5 dark:bg-muted/20">
        <h3 className="text-sm font-semibold tracking-tight text-foreground">
          {tp("requestDetail.fiscalRequiredTitle")}
        </h3>
        <p className="text-xs text-muted-foreground">
          {tp("requestDetail.fiscalRequiredBody")}
        </p>
        <div className="flex justify-end">
          <Button type="button" onClick={() => setFiscalDialogOpen(true)}>
            {tp("requestDetail.fiscalRequiredAction")}
          </Button>
        </div>
      </div>
    ) : (
      quoteSubmissionCard ?? quoteClosedCard
    );

    const quoteActivityCard = (
      <ReferenceSection label={tp("requestDetail.activityTitle")}>
        <RequestActivityList
          events={quoteActivityEvents}
          config={quoteActivityConfig}
          emptyMessage={tp("requestDetail.activityEmpty")}
        />
      </ReferenceSection>
    );

    return (
      <RequestDetailLayout
        title={quoteRequest.title}
        description={
          <>
            {quoteRequest.description}
            {quoteDeadlineNote}
          </>
        }
        onBack={() => navigate(backTarget)}
        primary={quoteAttachmentsCard}
        secondary={quoteAttachmentsCard ? quoteActivityCard : undefined}
        overlays={
          <>
          {/* Withdrawing pulls a live bid the client may already be comparing;
              the professional parity audit flagged this as the one destructive
              action on the page with no confirmation. */}
          <ConfirmationDialog
            open={withdrawConfirmOpen}
            onOpenChange={setWithdrawConfirmOpen}
            title={tp("requestDetail.quoteWithdrawConfirmTitle")}
            description={tp("requestDetail.quoteWithdrawConfirmDescription")}
            confirmText={tp("requestDetail.quoteWithdrawButton")}
            cancelText={t("pages.common.cancel")}
            variant="destructive"
            isLoading={withdrawQuoteMutation.isPending}
            onConfirm={() => withdrawQuoteMutation.mutate()}
          />
          <FiscalDeclarationDialog
            open={fiscalDialogOpen}
            onOpenChange={setFiscalDialogOpen}
            onSaved={() => {
              setFiscalDeclarationRequired(false);
              submitQuoteMutation.mutate();
            }}
          />
          </>
        }
      >
        {/* Before anything else: what half of the job is theirs, how the work
          splits, and which documents to expect. A price entered without this
          is a guess. */}
        <QuoteScopeCard
          myDiscipline={quoteRequest.discipline}
          otherDisciplines={quoteRequest.otherDisciplines ?? []}
          scopeRationale={quoteRequest.scopeRationale ?? null}
          anticipatedDocuments={quoteRequest.anticipatedDocuments ?? []}
          providedDocumentTypes={quoteProvidedDocumentTypes}
          intakeDetails={quoteRequest.intakeDetails ?? []}
        />
        {/* Directly under the scope card, because it answers the same kind of
          question: what am I being asked to do, and can I do it. The
          professional posts the parcel themselves now, so which department it
          goes to is part of pricing rather than an afterthought. */}
        {quoteRequest.delivery ? (
          <QuoteDeliveryDestinationCard
            zone={quoteRequest.delivery.zone}
            feeMinor={quoteRequest.delivery.feeMinor}
            adminArea={quoteRequest.delivery.adminArea}
            formattedAddress={quoteRequest.delivery.formattedAddress}
            courierNotes={quoteRequest.delivery.courierNotes}
            latitude={quoteRequest.delivery.latitude}
            longitude={quoteRequest.delivery.longitude}
          />
        ) : null}
        {/* Asking and pricing are the two things a professional does on this
          page, and they inform each other — side by side, the answer stays on
          screen while the price is typed. Paired only when both exist, so a
          professional who has already bid does not get a half-empty row. */}
        {quoteCardsArePaired ? (
          // The form is the reason the page exists, so it takes the wide
          // column and the conversation becomes a rail beside it. Two equal
          // halves gave a two-message thread the same width as a five-column
          // table, which squeezed the table and left a ~360px void under the
          // thread. Main-work-wide, panel-narrow is what QuickBooks, HoneyBook
          // and Airwallex all do with the same pair of concerns.
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_26rem] lg:items-start">
            {/* The activity log belongs to the main column, directly under the
              form, so it lines up with the card above it. Full-width it ran
              past the rail's right edge and read as a different page. */}
            <div className="space-y-4">
              {quoteMyBidCard}
              {quoteCard}
              {quoteAttachmentsCard ? null : quoteActivityCard}
            </div>
            {quoteQuestionsCard}
          </div>
        ) : (
          <>
            {quoteMyBidCard}
            {quoteCard}
            {quoteQuestionsCard}
            {quoteAttachmentsCard ? null : quoteActivityCard}
          </>
        )}
        {/* Mounted once for the page, outside both layout branches: a dialog
            is not a column, and mounting it inside either would give the two
            branches a dialog each. */}
        {quoteBreakdownDialog}
      </RequestDetailLayout>
    );
  }

  if (!id) {
    navigate("/requests", { replace: true });
    return <></>;
  }

  if (isLoading || !request) {
    return <LoadingPage />;
  }

  const statusRel =
    STATUS_LABEL_RELATIVE[request.status as NotarialRequestStatus];
  const statusLabel =
    statusRel != null ? tp(statusRel) : String(request.status);
  const statusVariant: StatusBadgeVariant =
    STATUS_VARIANTS[request.status as NotarialRequestStatus] ?? "info";
  const isAccepter = user?.id === request.acceptedByUserId;
  const isProfessionalPendingActivation =
    (user?.role === "notary" &&
      user.isNotary &&
      user.notaryActivationStatus === "pending") ||
    ((user?.role === "accountant" || user?.isAccountant === true) &&
      user.accountantActivationStatus === "pending");
  const fulfillmentResolved = request.fulfillmentMode ?? "physical_only";
  const allowsPhysical =
    fulfillmentResolved === "physical_only" ||
    fulfillmentResolved === "digital_and_physical";

  // WIP: quote_request needs a quote form here, not accept.
  const canAccept =
    request.status === "pending" &&
    !isProfessionalPendingActivation &&
    request.requestType !== "quote_request";
  const canRequestInfo = request.status === "in_progress" && isAccepter;
  // Asking for documents stays available while WAITING for them. canRequestInfo
  // is in_progress-only, which is right for the prose channel — it flips the
  // status — but wrong here: the professional's first request moves the request
  // to missing_information, and needing a seventh document after asking for six
  // is the normal case, not an edge one.
  const canRequestDocuments =
    isAccepter &&
    (request.status === "in_progress" ||
      request.status === "missing_information");
  // The server decides this now (see canUploadDocument /
  // certificateUploadStateFor): there are three ways in — the initial upload,
  // the professional's own 24h replacement window, and an open correction —
  // and re-deriving them here is how this predicate drifted from the server
  // gate before.
  const canUploadCertificate = isAccepter && request.canUploadDocument;
  /** Replacing a document that has already been delivered, not the first upload. */
  const isReplacingDocument =
    request.uploadMode === "window" || request.uploadMode === "correction";
  // Three different jobs behind one form, so it says which one it is. A
  // professional answering a correction and one fixing their own typo need
  // different confidence about what is about to happen to the delivered file.
  const uploadCardTitleKey = request.correctionOpen
    ? "requestDetail.correctionUploadCardTitle"
    : request.uploadMode === "window"
      ? "requestDetail.replaceUploadCardTitle"
      : null;
  const uploadDescriptionKey = request.correctionOpen
    ? "requestDetail.correctionUploadDescription"
    : request.uploadMode === "window"
      ? "requestDetail.replaceUploadDescription"
      : "requestDetail.digitalUploadDescription";
  const uploadSubmitKey = isReplacingDocument
    ? "requestDetail.replaceCertificateSubmit"
    : "requestDetail.uploadCertificateSubmit";
  /**
   * The three steps, each offered on exactly one status (spec f462e7d6 3.1).
   * Mutually exclusive by construction, which is what lets the card below hold
   * one message field and one pending flag for all of them.
   */
  const canMarkGestionReady =
    allowsPhysical && request.status === "in_progress" && isAccepter;
  const canMarkCannotComplete =
    (request.status === "in_progress" ||
      request.status === "missing_information" ||
      request.status === "document_ready_at_professional") &&
    isAccepter;
  const showReadyToDeliver =
    allowsPhysical &&
    request.status === "document_ready_at_professional" &&
    isAccepter;
  const showInTransit =
    allowsPhysical && request.status === "in_transit_to_consumer" && isAccepter;
  /**
   * The consumer's delivery address, shown at every one of those three steps
   * and before any of them (spec f462e7d6 3.3 — "before quoting, after
   * quoting, after award"). Null is possible on a request that predates the
   * mandatory-address change, which is why the card says so out loud rather
   * than rendering an empty line.
   */
  const deliveryAddress = request.deliveryAddressSnapshot?.trim() || null;
  // Hoisted above `showUploadForm`, which reads it. It used to sit beside the
  // other document lists further down; a const is in the temporal dead zone
  // until its declaration runs, so leaving it there threw at render.
  const professionalDocs = request.documents.filter(
    (d) => d.documentType === "notarial_certificate",
  );

  /**
   * The INLINE upload form shows only before anything has been delivered.
   *
   * Once a document exists, delivering again is a REPLACEMENT, and that is
   * reached from the delivered-work row instead — so the standing form would
   * be a second way to do the same thing, sitting open above the table that
   * shows what it would overwrite.
   */
  const slotInputs = {
    deliveredCount: professionalDocs.length,
    canUploadCertificate,
    isReplacingDocument,
  };
  const showUploadForm = showInlineUploadForm(slotInputs);
  const showFulfillmentCard =
    isAccepter &&
    (showUploadForm ||
      canMarkCannotComplete ||
      (allowsPhysical &&
        (canMarkGestionReady || showReadyToDeliver || showInTransit)));
  const isIncomeCertificateRequest =
    request.requestType === "income_certificate";
  const hideIncomeDependentApplicantRow =
    isIncomeCertificateRequest &&
    isIncomeCertificateDependentModality(request.incomeCertificateDetails);
  /**
   * The platform has stopped work on this request — typically while someone
   * checks that a bank transfer actually landed. Orthogonal to `status`, so it
   * is readable from nothing else in the payload.
   */
  const workPaused = request.pausedAt != null;

  const proBanners = (
    <>
      {/*
        First banner, and shown whatever else is true: every action below is
        locked, and a page full of dead buttons with the explanation further
        down reads as broken rather than paused.
      */}
      {workPaused ? (
        <Alert
          variant="default"
          className="mt-6 border-amber-500/50 bg-amber-500/10 dark:border-amber-500/40"
        >
          <PauseCircle className="text-amber-600 dark:text-amber-400" aria-hidden />
          <AlertTitle className="text-foreground">
            {tp("requestDetail.pausedTitle")}
          </AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {/*
              The admin's own words when they gave them. They are written for
              this professional, so they replace the generic line rather than
              being appended to it.
            */}
            {request.pauseReason?.trim()
              ? request.pauseReason
              : tp("requestDetail.pausedDescription")}
          </AlertDescription>
        </Alert>
      ) : null}
      {user?.role === "notary" &&
      user.isNotary &&
      user.notaryActivationStatus === "pending" ? (
        <Alert
          variant="info"
          className="mt-6 transition-opacity duration-150 motion-reduce:transition-none"
        >
          <Info className="text-blue-600 dark:text-blue-400" aria-hidden />
          <AlertTitle>{tp("requests.pendingActivationTitle")}</AlertTitle>
          <AlertDescription className="text-blue-900/90 dark:text-blue-100/90">
            {tp("requests.pendingActivationDescription")}
          </AlertDescription>
        </Alert>
      ) : null}
      {(user?.role === "accountant" || user?.isAccountant === true) &&
      user.accountantActivationStatus === "pending" ? (
        <Alert
          variant="info"
          className="mt-6 transition-opacity duration-150 motion-reduce:transition-none"
        >
          <Info className="text-blue-600 dark:text-blue-400" aria-hidden />
          <AlertTitle>
            {t("requests.user.accountantPendingReviewTitle")}
          </AlertTitle>
          <AlertDescription className="text-blue-900/90 dark:text-blue-100/90">
            {t("requests.user.accountantPendingReviewDescription")}
          </AlertDescription>
        </Alert>
      ) : null}
    </>
  );

  /**
   * The upload fields, shared by the inline card (first delivery) and the
   * replace dialog. ONE implementation: two copies of a file input, a validity
   * date and a submit button are two sets of rules about what may be
   * delivered, and they drift.
   */
  const certificateUploadFields = (
    <>
      <StyledFileInput
        id="professional-notarial-certificate-upload"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        value={certificateFile}
        onChange={setCertificateFile}
        disabled={isUploadingCertificate}
        label={tp("requestDetail.digitalUploadFileLabel")}
        hint="PDF, JPG, PNG, WebP"
      />
      <DatePickerField
        id="certificate-validity-date"
        label={tp("requestDetail.certificateValidityDateLabel")}
        value={certificateValidityDate}
        onChange={setCertificateValidityDate}
        disabled={isUploadingCertificate}
      />
    </>
  );

  const certificateSubmitButton = (
    <Button
      type="button"
      className="transition-colors duration-150"
      disabled={!certificateFile || isUploadingCertificate}
      onClick={() => void handleUploadCertificate()}
    >
      {isUploadingCertificate ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {tp("requestDetail.uploadCertificateSubmitting")}
        </>
      ) : (
        tp(uploadSubmitKey)
      )}
    </Button>
  );

  const proDialogs = (
    <>
      {/* The replace form, reached from the delivered-work row rather than
          standing open above it. Same fields and the same handler as the
          first-delivery card — `certificateUploadFields` is shared, so the two
          cannot disagree about what may be delivered. */}
      <Dialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {uploadCardTitleKey
                ? tp(uploadCardTitleKey)
                : tp("requestDetail.replaceDocumentAction")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {tp(uploadDescriptionKey)}
            </p>
            {request.correctionOpen && request.correctionReason ? (
              <blockquote className="border-l-2 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-foreground dark:bg-amber-900/20">
                {request.correctionReason}
              </blockquote>
            ) : null}
            {certificateUploadFields}
          </div>
          <DialogFooter>{certificateSubmitButton}</DialogFooter>
        </DialogContent>
      </Dialog>
      {/* FIRST in the overlay stack, and not dismissible. Every write behind
          this page is refused server-side until the CFE is recorded, so a
          professional who could close this would only discover that one failed
          action at a time. The controls underneath are disabled by WorkLock as
          well — the modal is the explanation, the fieldset is the enforcement,
          and the server is the authority. */}
      {rfqGate?.blocked ? (
        <ConsumerCfeGateModal
          legId={rfqGate.legId}
          blockers={rfqGate.blockers}
          onResolved={() => {
            void refetchRfqGate();
            void queryClient.invalidateQueries({ queryKey: ["notary", "request", id] });
          }}
        />
      ) : null}
      <Dialog
        open={cannotCompleteOpen}
        onOpenChange={(open) => {
          setCannotCompleteOpen(open);
          if (!open) setCannotCompleteNotes("");
        }}
      >
        <DialogContent className="sm:max-w-md motion-reduce:transition-none">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {tp("requestDetail.cannotCompleteDialogTitle")}
            </DialogTitle>
            <DialogDescription>
              {tp("requestDetail.cannotCompleteDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cannot-complete-notes" className="text-foreground">
              {tp("requestDetail.cannotCompleteNotesLabel")}
            </Label>
            <Textarea
              id="cannot-complete-notes"
              value={cannotCompleteNotes}
              onChange={(e) => setCannotCompleteNotes(e.target.value)}
              placeholder={tp("requestDetail.cannotCompleteNotesPlaceholder")}
              rows={5}
              disabled={cannotCompleteMutation.isPending}
              className="min-h-[120px] resize-y transition-colors duration-150"
            />
          </div>
          <DialogFooter className="flex w-full flex-row justify-between gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="transition-colors duration-150"
              onClick={() => setCannotCompleteOpen(false)}
              disabled={cannotCompleteMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="transition-colors duration-150"
              disabled={
                cannotCompleteMutation.isPending ||
                cannotCompleteNotes.trim().length < 10 ||
                !id
              }
              onClick={() => {
                if (!id) return;
                void cannotCompleteMutation.mutateAsync(
                  cannotCompleteNotes.trim(),
                );
              }}
            >
              {cannotCompleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {tp("requestDetail.cannotCompleteSubmitting")}
                </>
              ) : (
                tp("requestDetail.cannotCompleteConfirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company details dialog */}
      <Dialog open={companyDetailsOpen} onOpenChange={setCompanyDetailsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tp("requestDetail.companyDetailsTitle")}</DialogTitle>
          </DialogHeader>
          <DetailRows>
            <CompanyDetailRow
              label={tp("requestDetail.companyLegalName")}
              value={request.tenantName}
              bold
            />
            <CompanyDetailRow
              label={tp("requestDetail.companyRut")}
              value={request.tenantRut}
            />
            <CompanyDetailRow
              label={tp("requestDetail.companyAddress")}
              value={request.tenantAddress}
            />
            <CompanyDetailRow
              label={tp("requestDetail.companyCity")}
              value={request.tenantCity}
            />
            <CompanyDetailRow
              label={tp("requestDetail.companyDepartment")}
              value={request.tenantDepartment}
            />
          </DetailRows>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCompanyDetailsOpen(false)}
            >
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept + set estimated date dialog */}
      <Dialog
        open={acceptDateDialogOpen}
        onOpenChange={setAcceptDateDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {tp("requestDetail.acceptAndSetDateTitle")}
            </DialogTitle>
            <DialogDescription>
              {tp("requestDetail.acceptAndSetDateDescription")}
            </DialogDescription>
          </DialogHeader>
          <DatePickerField
            id="accept-estimated-date"
            label={tp("requestDetail.estimatedDateLabel")}
            value={acceptDateValue}
            onChange={setAcceptDateValue}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => void handleAcceptConfirm(false)}
            >
              {tp("requestDetail.skipForNow")}
            </Button>
            <Button
              onClick={() => void handleAcceptConfirm(true)}
              disabled={!acceptDateValue}
            >
              {tp("requestDetail.setDate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set/change estimated date dialog */}
      <Dialog
        open={estimatedDateDialogOpen}
        onOpenChange={setEstimatedDateDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {request.estimatedCompletionDate
                ? tp("requestDetail.editEstimatedDate")
                : tp("requestDetail.setEstimatedDate")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DatePickerField
              id="estimated-date-input"
              label={tp("requestDetail.estimatedDateLabel")}
              value={estimatedDateValue}
              onChange={setEstimatedDateValue}
            />
            {request.estimatedCompletionDate != null ? (
              <div className="space-y-2">
                <Label htmlFor="estimated-date-reason">
                  {tp("requestDetail.estimatedDateReason")}
                </Label>
                <Textarea
                  id="estimated-date-reason"
                  placeholder={tp(
                    "requestDetail.estimatedDateReasonPlaceholder",
                  )}
                  value={estimatedDateReason}
                  onChange={(e) => setEstimatedDateReason(e.target.value)}
                  rows={3}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEstimatedDateDialogOpen(false)}
              disabled={isSubmittingEstimatedDate}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void handleSetEstimatedDate()}
              disabled={
                !estimatedDateValue ||
                isSubmittingEstimatedDate ||
                (request.estimatedCompletionDate != null &&
                  !estimatedDateReason.trim())
              }
            >
              {isSubmittingEstimatedDate ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {tp("requestDetail.sending")}
                </>
              ) : (
                tp("requestDetail.saveDate")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request more info dialog */}
      <DocumentRequestDialog
        open={documentRequestOpen}
        onOpenChange={setDocumentRequestOpen}
        // No suggestions yet: the intake's anticipatedDocuments live on the
        // parent RFQ and this page does not fetch them. That pre-fill is D4 of
        // spec request-document-slots; the dialog already handles an empty list.
        onSubmit={(documents) => documentRequestMutation.mutate(documents)}
        isSubmitting={documentRequestMutation.isPending}
      />

      <Dialog open={requestInfoOpen} onOpenChange={setRequestInfoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tp("requestDetail.requestMoreInfo")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="request-info-message">
              {tp("requestDetail.missingInfoMessage")}
            </Label>
            <textarea
              id="request-info-message"
              placeholder={tp("requestDetail.missingInfoPlaceholder")}
              value={requestInfoMessage}
              onChange={(e) => setRequestInfoMessage(e.target.value)}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Optional, and visibly secondary to the message: most requests for
              information are a question, not a document handover. */}
          <div className="space-y-2 border-t border-border pt-4">
            <Label htmlFor="request-info-attachment">
              {tp("requestDetail.attachDocumentLabel")}
            </Label>
            <StyledFileInput
              id="request-info-attachment"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              value={attachmentFile}
              onChange={setAttachmentFile}
              disabled={isSubmittingInfo}
              hint={tp("requestDetail.attachDocumentHint")}
            />
            {attachmentFile ? (
              <div className="space-y-1.5">
                <Label htmlFor="request-info-attachment-type">
                  {tp("requestDetail.attachDocumentType")}
                </Label>
                <select
                  id="request-info-attachment-type"
                  value={attachmentType}
                  onChange={(e) =>
                    setAttachmentType(
                      e.target.value as ProfessionalAttachmentDocumentType,
                    )
                  }
                  disabled={isSubmittingInfo}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {PROFESSIONAL_ATTACHMENT_DOCUMENT_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`requests.professionalAttachmentType.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRequestInfoOpen(false)}
              disabled={isSubmittingInfo}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void handleSubmitRequestInfo()}
              disabled={!requestInfoMessage.trim() || isSubmittingInfo}
            >
              {isSubmittingInfo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isUploadingAttachment
                    ? tp("requestDetail.attachmentUploading")
                    : tp("requestDetail.sending")}
                </>
              ) : (
                tp("requestDetail.submitRequestInfo")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // The documents/activity pair are RequestDetailLayout's two footer slots.
  // They used to be assembled inside an IIFE in the layout's children, which
  // meant this page re-declared the very footer grid the layout owns -- and
  // put ~100 lines of filtering and a table renderer inside JSX, against the
  // project's no-inline-logic rule. Same markup, hoisted.
  // Three buckets, split on uploaderRole rather than on
  // `documentType === "notarial_certificate"`. That test was the only signal
  // of direction the data carried, and it worked only while the professional
  // could send exactly one kind of file. It cannot tell an attachment from a
  // consumer upload at all.
  //
  // The certificate keeps its own card: it is the paid-for deliverable, with
  // its own upload rules and replacement window, and burying it among drafts
  // would lose that distinction.
  const consumerDocs = request.documents.filter(
    (d) => d.uploaderRole === "consumer",
  );
  const professionalAttachments = request.documents.filter(
    (d) =>
      d.uploaderRole === "professional" &&
      d.documentType !== "notarial_certificate",
  );
  /**
   * One table for all three document lists on this page.
   *
   * `renderRowActions` is OPTIONAL and the two lists that pass nothing must
   * render exactly as before — the extra header cell and the extra body cell
   * only appear when a caller actually supplies actions. The delivered-work
   * list is the only one that does, so it is the only one that grows a column.
   */
  const renderDocTable = (
    docs: NotarialRequestDocumentDto[],
    emptyMsg: string,
    renderRowActions?: (doc: NotarialRequestDocumentDto) => ReactNode,
  ) =>
    docs.length === 0 ? (
      <p className="text-sm text-muted-foreground">{emptyMsg}</p>
    ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px] w-[220px]">
              {tp("requests.type")}
            </TableHead>
            <TableHead>{tp("requestDetail.documentName")}</TableHead>
            <TableHead className="w-[100px]">
              {tp("requestDetail.documentDate")}
            </TableHead>
            <TableHead className="w-12" />
            {renderRowActions ? <TableHead className="w-12" /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {docs.map((doc: NotarialRequestDocumentDto) => (
            <TableRow key={doc.id}>
              <TableCell className="align-middle">
                <Badge
                  variant="secondary"
                  className="font-normal normal-case whitespace-nowrap rounded-md px-3 py-1 text-xs"
                >
                  {t(
                    notarialDocumentTypeTranslationKey(
                      doc.documentType,
                      certificateLabelContext,
                    ),
                    doc.documentType,
                  )}
                </Badge>
              </TableCell>
              <TableCell>
                <AuthenticatedBlobDocumentLink
                  fetchBlob={() =>
                    fetchNotaryRequestDocumentBlob(id!, doc.id)
                  }
                  title={doc.originalFilename}
                  className="min-w-0 truncate hover:no-underline"
                >
                  {doc.originalFilename}
                </AuthenticatedBlobDocumentLink>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatDisplayDate(doc.createdAt)}
              </TableCell>
              <TableCell>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 transition-colors duration-150"
                  disabled={openingDocumentId === doc.id}
                  aria-label={tp("requestDetail.documentsOpen")}
                  onClick={() => {
                    void (async () => {
                      if (!id) return;
                      setOpeningDocumentId(doc.id);
                      try {
                        await openAuthenticatedBlobInNewTab(() =>
                          fetchNotaryRequestDocumentBlob(id, doc.id),
                        );
                      } catch {
                        toast.error(t("common.documentOpenError"));
                      } finally {
                        setOpeningDocumentId(null);
                      }
                    })();
                  }}
                >
                  {openingDocumentId === doc.id ? (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden
                    />
                  ) : (
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  )}
                </Button>
              </TableCell>
              {renderRowActions ? (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {renderRowActions(doc)}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

  const documentsColumn = (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
              <CardTitle className="text-base">
                Documentos del solicitante
              </CardTitle>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {/* Asking for documents belongs where the documents are. It
                  was in the activity card's header, beside "solicitar más
                  información", which is where you go to read history —
                  not where you go to think about what is missing. */}
                {canRequestInfo ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setDocumentRequestOpen(true)}
                  >
                    {t("notary.requestDetail.documentRequestButton")}
                  </Button>
                ) : null}
                {consumerDocs.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="transition-colors duration-150"
                    onClick={() => void handleDownloadAllDocumentsZip()}
                    disabled={downloadZipLoading}
                  >
                    {downloadZipLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    {tp("requestDetail.downloadAllDocumentsZip")}
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* What has been asked for, above what has arrived. The
                  professional's own list of outstanding slots — without
                  it, "Te pedí 6 documentos" in the timeline is the only
                  record of what they chose, and it cannot be added to or
                  corrected. */}
              {documentRequests.filter((d) => d.status !== "cancelled")
                .length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("notary.requestDetail.documentRequestListTitle")}
                  </p>
                  {documentRequests
                    .filter((d) => d.status !== "cancelled")
                    .map((doc) => {
                      const label =
                        doc.customLabel ??
                        t(
                          notarialDocumentTypeTranslationKey(
                            doc.documentType ?? "",
                            certificateLabelContext,
                          ),
                          { defaultValue: doc.documentType ?? "" },
                        );
                      const isFulfilled = doc.status === "fulfilled";
                      return (
                        <div
                          key={doc.id}
                          className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-foreground">
                              {label}
                            </p>
                            {doc.why ? (
                              <p className="truncate text-xs text-muted-foreground">
                                {doc.why}
                              </p>
                            ) : null}
                          </div>
                          <StatusBadge
                            variant={isFulfilled ? "success" : "warning"}
                          >
                            {t(
                              isFulfilled
                                ? "notary.requestDetail.documentRequestFulfilled"
                                : "notary.requestDetail.documentRequestPending",
                            )}
                          </StatusBadge>
                          {/* Only a pending slot can be withdrawn: a
                              fulfilled one has a document attached and
                              removing the ask would orphan it. */}
                          {!isFulfilled && canRequestDocuments ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() =>
                                cancelDocumentRequestMutation.mutate(doc.id)
                              }
                              disabled={
                                cancelDocumentRequestMutation.isPending
                              }
                              aria-label={t(
                                "notary.requestDetail.documentRequestCancel",
                              )}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      );
                    })}
                </div>
              ) : null}
            </CardContent>
            <CardContent>
              {request.status === "missing_information" &&
                consumerDocs.length > 0 && (
                  <p className="mb-3 text-sm text-muted-foreground">
                    {tp("requestDetail.companySubmittedDocsInfo")}
                  </p>
                )}
              {renderDocTable(
                consumerDocs,
                tp("requestDetail.noDocuments"),
              )}
            </CardContent>
          </Card>
          {/* Only once something has been sent. An empty card here would add a
              permanent slot to every request to advertise a feature most
              requests never use — the send action lives in the request-info
              dialog, where the professional is already writing. */}
          {professionalAttachments.length > 0 ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {tp("requestDetail.professionalAttachmentsTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderDocTable(
                  professionalAttachments,
                  tp("requestDetail.professionalAttachmentsEmpty"),
                )}
              </CardContent>
            </Card>
          ) : null}
          {/* The delivered-work card USED TO SIT HERE. It now renders in the
              fulfillment slot above, where the upload form used to stand —
              see `deliveredWorkCard`. The page was leading with an action on
              an artefact it had not yet shown, and burying the artefact. */}
        </div>
  );

  /**
   * What the professional delivered, in the slot the upload form used to hold.
   *
   * The row action is gated on `isReplacingDocument` as well as
   * `canUploadCertificate`: `uploadMode === 'initial'` is a permission to make
   * the FIRST delivery, and offering "Reemplazar" on a document that has never
   * been replaced would be a lie about what the button does.
   */
  const deliveredWorkCard =
    professionalDocs.length > 0 ? (
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
          <CardTitle className="text-base">
            {tp("requestDetail.professionalIssuedTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderDocTable(
            professionalDocs,
            tp("requestDetail.professionalIssuedEmpty"),
            showReplaceRowAction(slotInputs)
              ? () => (
                  <TableActionsMenu
                    actions={[
                      {
                        label: tp("requestDetail.replaceDocumentAction"),
                        icon: Upload,
                        onClick: () => setReplaceDialogOpen(true),
                      },
                    ]}
                  />
                )
              : undefined,
          )}
        </CardContent>
      </Card>
    ) : null;

  const activityColumn = (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
            <CardTitle className="text-base">
              {tp("requestDetail.historyTitle")}
            </CardTitle>
            {canRequestInfo ? (
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" onClick={() => setRequestInfoOpen(true)}>
                  {tp("requestDetail.requestMoreInfo")}
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-full p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={tp(
                        "requestDetail.requestMoreInfoTooltipAria",
                      )}
                    >
                      <Info className="h-4 w-4" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="end"
                    className="max-w-xs text-pretty motion-reduce:animate-none motion-reduce:fade-in-none"
                  >
                    {tp("requestDetail.requestMoreInfoTooltip")}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-3">
            {showStatusVersusHistoryHint ? (
              <p
                className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground motion-reduce:transition-none"
                role="note"
              >
                {tp("requestDetail.statusVersusHistoryHint")}
              </p>
            ) : null}
            <RequestActivityList
              events={request.events.map((e) => ({
                id: e.id,
                type: e.eventType,
                createdAt: e.createdAt,
                actorDisplayName: e.actorDisplayName,
                actorRole: e.actorRole,
                message: e.message,
              }))}
              config={notarialActivityConfig(request.tenantName)}
              emptyMessage={tp("requestDetail.historyEmpty")}
            />
          </CardContent>
        </Card>
  );

  return (
    <RequestDetailLayout
      title={requestPageTitle}
      description={tpo("requestDetail.idLabel", { id: id.slice(0, 8) })}
      onBack={() => navigate(backTarget)}
      banner={proBanners}
      overlays={proDialogs}
      primary={<WorkLock locked={workPaused || cfeGateBlocked}>{documentsColumn}</WorkLock>}
      secondary={<WorkLock locked={workPaused || cfeGateBlocked}>{activityColumn}</WorkLock>}
    >
      <WorkLock locked={workPaused || cfeGateBlocked} className="space-y-6">
      {request.pricing ? (
        <ProfessionalRequestPricingCard
          pricing={request.pricing}
          requestType={request.requestType}
          requestStatus={request.status}
        />
      ) : null}
      {isIncomeCertificateRequest ? (
        <IncomeCertificateSummaryCard
          details={request.incomeCertificateDetails}
        />
      ) : null}

      {request.companyRutUnverified ? (
        <Alert
          variant="default"
          className="border-amber-500/50 bg-amber-500/10 dark:border-amber-500/40"
        >
          <AlertTitle className="text-foreground">
            {tp("requestDetail.companyRutUnverifiedTitle")}
          </AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {tp("requestDetail.companyRutUnverifiedDescription")}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {tp("requestDetail.detailsTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DetailRows>
            {hideIncomeDependentApplicantRow ? null : (
              <DetailRow label={tp("requestDetail.companyLabel")} valueClassName="flex items-center gap-2 text-sm text-foreground">
                {request.tenantName ?? request.requesterName ?? "—"}
                {request.tenantName ? (
                  <button
                    type="button"
                    className="text-xs text-primary underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded transition-colors duration-150"
                    onClick={() => setCompanyDetailsOpen(true)}
                  >
                    {tp("requestDetail.viewCompanyDetails")}
                  </button>
                ) : null}
              </DetailRow>
            )}
            {/* No conditional `first:pt-0` here any more: DetailRow carries it
                as a `:first-child` rule, so the row pads correctly whether or
                not the applicant row above it rendered. */}
            <DetailRow
              label={tp("requestDetail.requester")}
              valueClassName="space-y-0.5"
            >
              <p className="text-sm text-foreground">
                {request.requesterName ?? "—"}
              </p>
              {request.requesterDocumentType &&
                request.requesterDocumentNumber && (
                  <p className="text-xs text-muted-foreground">
                    {request.requesterDocumentType}{" "}
                    {request.requesterDocumentNumber}
                  </p>
                )}
            </DetailRow>
            <DetailRow label={tp("requests.type")}>
              {requestTypeLabel}
            </DetailRow>
            <DetailRow label={"Modalidad"}>
              {fulfillmentResolved === "digital_only"
                ? "Digital"
                : fulfillmentResolved === "physical_only"
                  ? "Físico"
                  : "Digital y físico"}
            </DetailRow>
            <DetailRow label={tp("requests.status")}>
              {request.fulfillmentStage ? (
                <div className="inline-flex flex-wrap items-center gap-2">
                  <StatusBadge variant="success">Completado</StatusBadge>
                  <FulfillmentStageChip stage={request.fulfillmentStage} />
                </div>
              ) : (
                <StatusBadge variant={statusVariant}>
                  {statusLabel}
                </StatusBadge>
              )}
            </DetailRow>
            <DetailRow label={tp("requests.createdAt")}>
              {formatDisplayDate(request.createdAt)}
            </DetailRow>
          </DetailRows>

          {/* The `!showPickupAddressForm` guard here is gone with the form it
              guarded against: there is no multi-step pickup form to collide
              with any more, so "no puedo completarla" is simply available
              whenever the status allows it. */}
          {canAccept || canMarkCannotComplete ? (
            <div className="flex w-full flex-wrap justify-end gap-2">
              {canMarkCannotComplete ? (
                <Button
                  type="button"
                  variant="outline"
                  className="transition-colors duration-150"
                  onClick={() => {
                    setCannotCompleteNotes("");
                    setCannotCompleteOpen(true);
                  }}
                >
                  <Ban className="mr-2 h-4 w-4" />
                  {tp("requestDetail.cannotCompleteRequest")}
                </Button>
              ) : null}
              {canAccept ? (
                <Button
                  className="transition-colors duration-150"
                  onClick={handleAcceptClick}
                  disabled={isAccepting}
                >
                  {isAccepting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {tp("requestDetail.accepting")}
                    </>
                  ) : (
                    tp("requestDetail.accept")
                  )}
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Estimated completion date card — visible after acceptance */}
      {isAccepter &&
      (request.status === "in_progress" ||
        request.status === "missing_information" ||
        request.status === "document_ready_at_professional") ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  {tp("requestDetail.estimatedCompletionDate")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {request.estimatedCompletionDate
                    ? new Date(
                        request.estimatedCompletionDate,
                      ).toLocaleDateString("es-UY", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        timeZone: "UTC",
                      })
                    : "—"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEstimatedDateValue(
                  request.estimatedCompletionDate
                    ? new Date(request.estimatedCompletionDate)
                        .toISOString()
                        .slice(0, 10)
                    : "",
                );
                setEstimatedDateReason("");
                setEstimatedDateDialogOpen(true);
              }}
            >
              {request.estimatedCompletionDate
                ? tp("requestDetail.editEstimatedDate")
                : tp("requestDetail.setEstimatedDate")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {request.missingDocumentNotes &&
        Object.keys(request.missingDocumentNotes).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {tp("requestDetail.missingDocumentsTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {tp("requestDetail.missingDocumentsIntro")}
              </p>
              <dl className="space-y-2">
                {Object.entries(request.missingDocumentNotes).map(
                  ([docType, note]) => (
                    <div
                      key={docType}
                      className="rounded-md border border-border bg-muted/30 px-3 py-2"
                    >
                      <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {t(
                          notarialDocumentTypeTranslationKey(
                            docType,
                            certificateLabelContext,
                          ),
                          docType,
                        )}
                      </dt>
                      <dd className="mt-1 text-sm text-foreground">
                        {note.trim() ? (
                          <span className="whitespace-pre-wrap">{note}</span>
                        ) : (
                          <span className="italic text-muted-foreground">
                            —
                          </span>
                        )}
                      </dd>
                    </div>
                  ),
                )}
              </dl>
            </CardContent>
          </Card>
        )}

      {/* The delivered work takes the slot the upload form used to hold.
          Rendered BEFORE the fulfillment card, which from here on carries only
          the physical pickup / notify-ready machinery once something has been
          delivered — `showUploadForm` suppresses its upload half. */}
      {deliveredWorkCard}

      {showFulfillmentCard ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {showInTransit
                ? tp("requestDetail.gestionInTransitCardTitle")
                : showReadyToDeliver
                  ? tp("requestDetail.gestionReadyCardTitle")
                  : showUploadForm && uploadCardTitleKey
                    ? tp(uploadCardTitleKey)
                    : showUploadForm && !allowsPhysical
                      ? tp("requestDetail.digitalUploadCardTitle")
                      : showUploadForm &&
                          allowsPhysical &&
                          request.status === "in_progress"
                        ? tp("requestDetail.mixedFulfillmentCardTitle")
                        : tp("requestDetail.gestionDeliveryCardTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {request.status === "missing_information" ? (
              <p className="text-sm text-muted-foreground">
                {tp("requestDetail.completeRequestBlockedWhileMissingInfo")}
              </p>
            ) : null}
            {showUploadForm ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {tp(uploadDescriptionKey)}
                </p>
                {/* The requester's own words, quoted. A professional told only
                    that "a correction was requested" has to leave the page to
                    find out what to fix. */}
                {request.correctionOpen && request.correctionReason ? (
                  <blockquote className="border-l-2 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-foreground dark:bg-amber-900/20">
                    {request.correctionReason}
                  </blockquote>
                ) : null}
                {certificateUploadFields}
                <div className="flex flex-row justify-end">
                  {certificateSubmitButton}
                </div>
              </div>
            ) : null}
            {showUploadForm && allowsPhysical ? (
              <Separator className="bg-border" />
            ) : null}

            {/* THE DELIVERY ADDRESS, AT EVERY STEP (spec f462e7d6 3.3). The
                professional reads it to price the trip, to plan it, and to
                make it — so it is not tucked behind the action of the moment.
                No phone and no email: the address is what delivering needs, a
                contact channel is not, and the platform keeps every
                consumer-professional conversation on the request. */}
            {allowsPhysical &&
            (canMarkGestionReady || showReadyToDeliver || showInTransit) ? (
              <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-4 dark:bg-muted/20">
                <h3 className="text-sm font-semibold text-foreground">
                  {tp("requestDetail.gestionDeliveryAddressLabel")}
                </h3>
                {deliveryAddress ? (
                  <>
                    <p className="whitespace-pre-wrap text-sm text-foreground">
                      {deliveryAddress}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {tp("requestDetail.gestionDeliveryAddressHint")}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {tp("requestDetail.gestionDeliveryAddressMissing")}
                  </p>
                )}
              </div>
            ) : null}

            {/* ONE optional message, whichever step is on screen — the three
                are mutually exclusive by status. It reaches the applicant on
                the timeline for mark-ready, and in the notification for the
                other two. */}
            {canMarkGestionReady || showReadyToDeliver || showInTransit ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {tp(
                    showInTransit
                      ? "requestDetail.gestionDeliveredDescription"
                      : showReadyToDeliver
                        ? "requestDetail.gestionInTransitDescription"
                        : "requestDetail.gestionMarkReadyDescription",
                  )}
                </p>
                {request.status !== "missing_information" ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="notary-delivery-message">
                        {tp(
                          canMarkGestionReady
                            ? "requestDetail.gestionMarkReadyNotesLabel"
                            : "requestDetail.gestionDeliveryMessageLabel",
                        )}
                      </Label>
                      <Textarea
                        id="notary-delivery-message"
                        placeholder={tp(
                          canMarkGestionReady
                            ? "requestDetail.gestionMarkReadyNotesPlaceholder"
                            : "requestDetail.gestionDeliveryMessagePlaceholder",
                        )}
                        value={deliveryMessage}
                        onChange={(e) => setDeliveryMessage(e.target.value)}
                        rows={2}
                        disabled={isTransitioningDelivery}
                        className="transition-colors duration-150"
                      />
                    </div>
                    <div className="flex w-full flex-row flex-wrap justify-end gap-2">
                      <Button
                        type="button"
                        className="transition-colors duration-150"
                        disabled={isTransitioningDelivery}
                        onClick={() =>
                          void runDeliveryTransition(
                            showInTransit
                              ? "delivered"
                              : showReadyToDeliver
                                ? "inTransit"
                                : "ready",
                          )
                        }
                      >
                        {isTransitioningDelivery ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {tp(
                              showInTransit
                                ? "requestDetail.gestionDeliveredSubmitting"
                                : showReadyToDeliver
                                  ? "requestDetail.gestionInTransitSubmitting"
                                  : "requestDetail.gestionMarkReadySubmitting",
                            )}
                          </>
                        ) : (
                          tp(
                            showInTransit
                              ? "requestDetail.gestionDeliveredSubmit"
                              : showReadyToDeliver
                                ? "requestDetail.gestionInTransitSubmit"
                                : "requestDetail.gestionMarkReadySubmit",
                          )
                        )}
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      </WorkLock>
    </RequestDetailLayout>
  );
}
